import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  OutlinedInput,
} from '@mui/material'
import api from '../../../services/api'

/**
 * Shared config component for on-demand pathway types (information,
 * resource_finder). Binds the pathway to one or more content groups —
 * the corpus containers whose data the pathway's agent searches at
 * runtime (multiple groups are unioned; scope is pinned server-side).
 */
function ContentGroupsConfig({ config = {}, onChange }) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true)
      setLoadError(null)
      try {
        const response = await api.get('/content-groups')
        setGroups(response.data || [])
      } catch (error) {
        console.error('Failed to fetch content groups:', error)
        setLoadError('Failed to load content groups')
        setGroups([])
      } finally {
        setLoading(false)
      }
    }
    fetchGroups()
  }, [])

  const selectedIds = config.content_group_ids || []

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Content Groups
      </Typography>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {selectedIds.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This pathway requires at least one content group — the agent has no
          searchable content without one.
        </Alert>
      )}

      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Content groups</InputLabel>
        <Select
          multiple
          value={selectedIds}
          input={<OutlinedInput label="Content groups" />}
          onChange={(e) => onChange({ ...config, content_group_ids: e.target.value })}
          disabled={loading}
          startAdornment={loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          renderValue={(ids) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {ids.map((id) => {
                const group = groups.find((g) => g.id === id)
                return <Chip key={id} label={group ? group.name : `#${id}`} size="small" />
              })}
            </Box>
          )}
        >
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              <Box>
                <Typography variant="body2">{g.name}</Typography>
                {g.description && (
                  <Typography variant="caption" color="text.secondary">
                    {g.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="caption" color="text.secondary">
        Patients on this intervention see content from all selected groups
        combined. Groups are managed on the Content Groups page.
      </Typography>
    </Box>
  )
}

export default ContentGroupsConfig
